<template>
    <div id="formworkstationtypes">
        <pre
            class="debug"
            v-if="isDebug"
            >{{ { workstationType, rules, isValid, validations } }}</pre
        >
        <div class="top-buttons">
            <ui5-button
                @click="saveChanges"
                :disabled="saving"
            >
                <spinner v-if="saving" />
                {{ trans('pages.form_workstation_types.save') }}
            </ui5-button>
            <ui5-button @click="goBack">
                {{ trans('pages.form_workstation_types.cancel') }}
            </ui5-button>
        </div>

        <loader v-if="loading" />
        <template v-else>
            <div class="settings-column">
                <ui5-label>
                    {{ trans('pages.form_workstation_types.name') }}
                    <i
                        class="fa fa-question-circle"
                        @click="$refs.tooltipName.showAt($event.target)"
                    />
                </ui5-label>
                <ui5-input
                    type="Text"
                    :value="workstationType.name"
                    @input="workstationType.name = $event.target.value"
                />
                <ui5-popover ref="tooltipName">
                    {{ trans('pages.form_workstation_types.name_tooltip') }}
                </ui5-popover>
            </div>

            <div class="settings-column">
                <ui5-label>
                    {{ trans('pages.form_workstation_types.external_name') }}
                    <i
                        class="fa fa-question-circle"
                        @click="$refs.tooltipExternalName.showAt($event.target)"
                    />
                </ui5-label>
                <ui5-input
                    type="Text"
                    :value="workstationType.externalName"
                    @input="workstationType.externalName = $event.target.value"
                />
                <span
                    class="error"
                    v-show="!validations.externalName"
                >
                    {{
                        trans(
                            'pages.form_workstation_types.external_name_error'
                        )
                    }}
                </span>
                <ui5-popover ref="tooltipExternalName">
                    {{
                        trans(
                            'pages.form_workstation_types.external_name_tooltip'
                        )
                    }}
                </ui5-popover>
            </div>

            <div class="settings-column">
                <ui5-label>
                    {{ trans('pages.form_workstation_types.description') }}
                    <i
                        class="fa fa-question-circle"
                        @click="$refs.tooltipDescription.showAt($event.target)"
                    />
                </ui5-label>
                <ui5-input
                    type="Text"
                    :value="workstationType.description"
                    @input="workstationType.description = $event.target.value"
                />
                <ui5-popover ref="tooltipDescription">
                    {{
                        trans(
                            'pages.form_workstation_types.description_tooltip'
                        )
                    }}
                </ui5-popover>
            </div>

            <div class="settings-column">
                <ui5-label>
                    {{ trans('pages.form_workstation_types.order') }}
                    <i
                        class="fa fa-question-circle"
                        @click="$refs.tooltipOrder.showAt($event.target)"
                    />
                </ui5-label>
                <text-input
                    type="number"
                    min="1"
                    v-model="workstationType.order"
                />
                <span
                    class="error"
                    v-show="!validations.order"
                >
                    {{ trans('pages.form_workstation_types.order_error') }}
                </span>
                <ui5-popover ref="tooltipOrder">
                    {{ trans('pages.form_workstation_types.order_tooltip') }}
                </ui5-popover>
            </div>

            <div class="settings-column">
                <ui5-label>
                    {{
                        trans('pages.form_workstation_types.menu_button_color')
                    }}
                    <i
                        class="fa fa-question-circle"
                        @click="$refs.tooltipColor.showAt($event.target)"
                    />
                </ui5-label>
                <ui5-color-picker
                    :color="workstationType.color"
                    @change="workstationType.color = getHexColor($event.target)"
                ></ui5-color-picker>
                <ui5-popover ref="tooltipColor">
                    {{
                        trans(
                            'pages.form_workstation_types.menu_button_color_tooltip'
                        )
                    }}
                </ui5-popover>
            </div>

            <div class="settings-column">
                <ui5-label>
                    {{ trans('pages.form_workstation_types.reporting_color') }}
                    <i
                        class="fa fa-question-circle"
                        @click="
                            $refs.tooltipReportingColor.showAt($event.target)
                        "
                    />
                </ui5-label>
                <ui5-color-picker
                    :color="workstationType.reportingColor"
                    @change="
                        workstationType.reportingColor = getHexColor(
                            $event.target
                        )
                    "
                ></ui5-color-picker>
                <ui5-popover ref="tooltipReportingColor">
                    {{
                        trans(
                            'pages.form_workstation_types.reporting_color_tooltip'
                        )
                    }}
                </ui5-popover>
            </div>

            <div
                class="settings-column"
                data-property-name="showInProgress"
            >
                <ui5-label>
                    {{ trans('pages.form_workstation_types.show_in_progress') }}
                    <i
                        class="fa fa-question-circle"
                        @click="
                            $refs.tooltipShowInProgress.showAt($event.target)
                        "
                    />
                </ui5-label>
                <ui5-select
                    @change="
                        workstationType.showInProgress =
                            $event.detail.selectedOption.value
                    "
                >
                    <ui5-option
                        :value="1"
                        :selected="workstationType.showInProgress == 1"
                    >
                        {{ trans('pages.form_workstation_types.yes') }}
                    </ui5-option>
                    <ui5-option
                        :value="0"
                        :selected="workstationType.showInProgress == 0"
                    >
                        {{ trans('pages.form_workstation_types.no') }}
                    </ui5-option>
                </ui5-select>
                <ui5-popover ref="tooltipShowInProgress">
                    {{
                        trans(
                            'pages.form_workstation_types.show_in_progress_tooltip'
                        )
                    }}
                </ui5-popover>
            </div>
        </template>
    </div>
</template>
<script>
import IdParamMixin from '../mixins/id-param';
import IsDebugMixin from '../mixins/is-debug';
import ColorPickerMixin from '../mixins/color-picker';
import workstationApi from '../api/workstation';
import workstationTypeApi from '../api/workstationType';
import Loader from '../components/loader/Loader';
import Spinner from '../components/loader/Spinner';
import UI5Helpers from '../helpers/UI5Helpers';
import { showInfoMessage, showErrorMessage } from '../dialogs';
import localization from '../mixins/localization';
import TextInput from '../components/TextInput.vue';

export default {
    components: { Spinner, Loader, TextInput },
    mixins: [IdParamMixin(), IsDebugMixin, ColorPickerMixin, localization],
    data() {
        return {
            loading: false,
            saving: false,
            workstationType: {
                name: '',
                externalName: '',
                description: '',
                order: '',
                color: 'white',
                reportingColor: 'white',
                showInProgress: false,
            },
            rules: [],
        };
    },
    mounted() {
        this.loading = true;
        const promises = [];
        if (this.idParam) {
            promises.push(
                workstationTypeApi
                    .fetchTypeById(parseInt(this.idParam))
                    .catch(() => {
                        showErrorMessage(
                            this.trans(
                                'pages.form_workstation_types.workstation_type_not_found'
                            )
                        );
                    })
                    .then((response) => {
                        const workstation = response.data.data;
                        this.workstationType.name = workstation.name || '';
                        this.workstationType.externalName =
                            workstation.externalName || '';
                        this.workstationType.description =
                            workstation.description || '';
                        this.workstationType.order = workstation.order || '';
                        this.workstationType.color = workstation.color || '';
                        this.workstationType.reportingColor =
                            workstation.reportingColor || '';
                        this.workstationType.showInProgress =
                            workstation.showInProgress || false;
                    })
            );
        }
        promises.push(
            workstationApi.fetchTypeValidationRules().then((response) => {
                this.rules = response.data.data;
            })
        );
        Promise.all(promises).then(() => {
            this.loading = false;
        });
    },
    computed: {
        endpointMethod() {
            if (this.idParam) {
                return 'updateType';
            } else {
                return 'addType';
            }
        },
        validations() {
            const validations = {};
            if (this.rules) {
                for (const [propertyName, rule] of Object.entries(this.rules)) {
                    const value = this.workstationType[propertyName];
                    const valid = this.validateProperty(rule, value);
                    validations[propertyName] = valid;
                }
            }
            return validations;
        },
        isValid() {
            return Object.values(this.validations).every((x) => x);
        },
    },
    methods: {
        getHexColor: UI5Helpers.getHexColorFromColorPicker,
        goBack() {
            this.$router.push('/settingsworkstationtypes');
        },
        validateProperty(rule, value) {
            switch (rule) {
                case 'notEmpty':
                    return value.toString().length > 0;
                case 'integerGreaterThan0':
                    return value.toString().match(/^[0-9]+$/) && value > 0;
            }
            return true;
        },
        saveChanges() {
            if (!this.isValid) {
                showErrorMessage(
                    this.trans(
                        'pages.form_workstation_types.fill_fields_correctly'
                    )
                );
            } else {
                this.sendToServer();
            }
        },
        sendToServer() {
            this.saving = true;
            const payload = { id: this.idParam, ...this.workstationType };
            const method = workstationTypeApi[this.endpointMethod];
            method(payload)
                .then(() => {
                    showInfoMessage(
                        this.trans(
                            'pages.form_workstation_types.workstation_type_saved'
                        )
                    );
                    this.goBack();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans(
                            'pages.form_workstation_types.unknown_save_error'
                        )
                    );
                })
                .finally(() => {
                    this.saving = false;
                });
        },
    },
};
</script>
